import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { App, Button, Form, Input, Result } from 'antd';
import { Controller, useForm } from 'react-hook-form';
import { authApi } from '../auth.api';
import { resetPasswordSchema, type ResetPasswordForm } from '../auth.schemas';

export function ResetPasswordPage() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const token = new URLSearchParams(window.location.search).get('token') ?? '';

  const form = useForm<ResetPasswordForm>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { password: '', confirmPassword: '' },
  });

  const mutation = useMutation({
    mutationFn: (values: ResetPasswordForm) =>
      authApi.resetPassword({ token, password: values.password }),
    onSuccess: () => {
      message.success('Password updated. Please sign in.');
      void navigate({ to: '/login' });
    },
    onError: (error) => message.error(error instanceof Error ? error.message : 'Reset failed'),
  });

  if (!token) {
    return (
      <Result
        status="error"
        title="Missing reset token"
        extra={
          <Link to="/forgot-password">
            <Button type="primary">Request a new link</Button>
          </Link>
        }
      />
    );
  }

  return (
    <Form layout="vertical" onFinish={form.handleSubmit((v) => mutation.mutate(v))}>
      <Controller
        control={form.control}
        name="password"
        render={({ field, fieldState }) => (
          <Form.Item
            label="New password"
            validateStatus={fieldState.error ? 'error' : ''}
            help={fieldState.error?.message}
          >
            <Input.Password {...field} autoComplete="new-password" placeholder="••••••••" />
          </Form.Item>
        )}
      />
      <Controller
        control={form.control}
        name="confirmPassword"
        render={({ field, fieldState }) => (
          <Form.Item
            label="Confirm password"
            validateStatus={fieldState.error ? 'error' : ''}
            help={fieldState.error?.message}
          >
            <Input.Password {...field} autoComplete="new-password" placeholder="••••••••" />
          </Form.Item>
        )}
      />
      <Button type="primary" htmlType="submit" block loading={mutation.isPending}>
        Update password
      </Button>
    </Form>
  );
}
